// Journals (SuiteModel/Journal.swift): post statuses, the post fields `ensureJournalFields` adds, and the numbers posts
// would take if they went out now (`provisionalNumbers`, as compile's `wrapPosts` gives them). The next number and the
// numbering's problems are in ../journal.ts; the writes (New Post, `numberPosts`, the first number) in edits.ts.
import type { MetadataValue } from '../contract.ts';
import { JournalPosts } from '../edit/editProject.ts';
import { ModelError, type FieldSpec } from '../edit/storeEditor.ts';
import { nextPostNumber } from '../journal.ts';

export const JOURNAL_STATUS = {
  idea: 'status-idea',
  draft: JournalPosts.draft,
  scheduled: JournalPosts.scheduled,
  published: JournalPosts.published,
} as const;

/** `JournalPosts.statusOptions`, in workflow order. */
export const JOURNAL_STATUS_OPTIONS = [
  { id: JOURNAL_STATUS.idea, name: 'Idea' }, { id: JOURNAL_STATUS.draft, name: 'Draft' },
  { id: JOURNAL_STATUS.scheduled, name: 'Scheduled' }, { id: JOURNAL_STATUS.published, name: 'Published' },
] as const;

/** `JournalPosts.numberedStatuses`: statuses that give a post its number. */
export const NUMBERED_STATUSES: ReadonlySet<string> = new Set([JOURNAL_STATUS.scheduled, JOURNAL_STATUS.published]);

/** `ensureJournalFields`: the post fields (number, publish date, tags) a journal lacks, ordered after the existing ones. */
export function missingJournalFields(existing: string[]): FieldSpec[] {
  const have = new Set(existing);
  const order = have.size;
  const fields: FieldSpec[] = [
    { id: 'post-number', name: 'Post number', type: 'number', order },
    { id: 'publish-date', name: 'Publish date', type: 'date', order: order + 1 },
    { id: 'tags', name: 'Tags', type: 'text', order: order + 2 },
  ];
  return fields.filter((f) => !have.has(f.id));
}

/** A post's status is one that gives it a number. */
export const isNumberedStatus = (metadata: Record<string, MetadataValue> | undefined) => {
  const s = metadata?.status;
  return s?.type === 'text' && NUMBERED_STATUSES.has(s.value);
};

/** `provisionalNumbers`: for the posts given (in the order they're compiled), the number each without one would take
 *  if it went out now — the next number, then one more for each after it. */
export function provisionalNumbers(posts: string[], metadataOf: (id: string) => Record<string, MetadataValue> | undefined,
  allMetadata: Iterable<Record<string, MetadataValue> | undefined>, first: number | null): Map<string, number> {
  let next = nextPostNumber(allMetadata, first);
  const out = new Map<string, number>();
  for (const id of posts) {
    if (JournalPosts.number(metadataOf(id)) !== null || out.has(id)) continue;
    out.set(id, next++);
  }
  return out;
}

/** `setFirstPostNumber`'s rule: a number above 1 is stored (it must be below the limit); 1 or none clears it. */
export function validFirstPostNumber(n: number | null): number | null {
  if (n === null || !(n > 1)) return null;
  if (!Number.isInteger(n) || n >= JournalPosts.numberLimit) throw new ModelError('invalid', 'A post number must be below 1,000,000,000.');
  return n;
}
